import { useEffect } from 'react';
import {
  DraftedProductsResponse,
  CapacityManagementRootResponse,
  DraftedProductsParams,
} from 'src/apis/capacity-management/types';
import { CapacityManagementRootRequest } from 'src/apis/capacity-management/use-capacity-management-root';
import { useAxios } from 'src/hooks/use-axios';
import { Forbidden } from 'src/utils/errors';
import urlTemplate from 'url-template';

type HookConfig = {
  capacityManagementRoot: CapacityManagementRootRequest;
  params?: DraftedProductsParams;
};

type RequestOptions = {
  rootData: CapacityManagementRootResponse;
  params?: DraftedProductsParams;
};

export function useDraftedProducts({
  capacityManagementRoot,
  params,
}: HookConfig) {
  const draftedProducts = useAxios(
    (axios, signal, { rootData, params }: RequestOptions) => {
      if (!rootData._links?.draftedProducts) throw new Forbidden();

      const url = urlTemplate
        .parse(rootData._links.draftedProducts.href)
        .expand({});

      return axios.request<DraftedProductsResponse>({
        signal,
        url,
        params,
      });
    },
    { neededOnPageLoad: true }
  );

  useEffect(() => {
    if (!capacityManagementRoot.response) return;

    const rootData = capacityManagementRoot.response.data;
    const options = { rootData, params };

    draftedProducts.execute(options);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capacityManagementRoot.response, params]);

  return draftedProducts;
}

export type DraftedProductsRequest = ReturnType<typeof useDraftedProducts>;
